import { FC, useRef, Fragment, useState, useEffect, useCallback} from 'react';
import { Formik, Form, Field, FormikProps } from 'formik';
import { dark as darkColors, secondary } from 'shared/theme/colorTokens';
import { CircularProgress, TextField, Chip, Dialog } from '@material-ui/core';
import { IconButton, Typography } from '@material-ui/core';
import { Edit as EditIcon, Delete as DeleteIcon } from '@material-ui/icons';
import Button from 'shared/components/Button';
import { Query } from '@vadiun/react-hooks-legacy';
import { ChoferPartialModel } from 'app/chofer/models/Chofer';
import {
  Autocomplete,
  AutocompleteRenderInputParams
} from 'formik-material-ui-lab';
import { useThemeContext } from 'shared/context/themeContext';

interface DriversNotesProps {
  driversQuery: Query<ChoferPartialModel[]>;
  initialValue?: string;
  isOpen: boolean;
  handleClose: () => void;
  onSave?: (value: string) => void;
  travelId: number;
}


const DriversNotesModal: FC<DriversNotesProps> = ({
  driversQuery,
  initialValue = '',
  onSave,
  isOpen,
  handleClose,
  travelId
}) => {
   const { theme } = useThemeContext();
    const isDark = theme === 'dark';
  const formRef =
    useRef<FormikProps<{ preassigned_driver_draft: string }>>(null);

  const [selectedDrivers, setSelectedDrivers] = useState<ChoferPartialModel[]>(
    []
  );

  // Parse initial value to array of driver codes
  const parseInitialValue = useCallback(
    (value: string): ChoferPartialModel[] => {
      if (!value || !driversQuery.data) return [];
      const codes = value.split(' ').filter((code) => code.trim() !== '');
      return driversQuery.data.filter((driver) =>
        codes.includes(driver.codigo.toString())
      );
    },
    [driversQuery.data]
  );

  const handleCloseModal = () => {
    setSelectedDrivers([]);
    formRef.current?.resetForm();
    handleClose();
  };

  const handleSave = (_values: { preassigned_driver_draft: string }) => {
    const driverCodesString = selectedDrivers
      .map((driver) => driver.codigo.toString())
      .join(' ');
    onSave?.(driverCodesString ?? '');
    setSelectedDrivers([]);
    formRef.current?.resetForm();
  };

  const handleDriverSelect = (newDriver: ChoferPartialModel | null) => {
    if (
      newDriver &&
      !selectedDrivers.find((driver) => driver.codigo === newDriver.codigo)
    ) {
      setSelectedDrivers((prev) => [...prev, newDriver]);
    }
  };

  const handleRemoveDriver = (driverCode: string) => {
    setSelectedDrivers((prev) =>
      prev.filter((driver) => driver.codigo.toString() !== driverCode)
    );
  };

  useEffect(() => {
    if (driversQuery.data && initialValue) {
      setSelectedDrivers(parseInitialValue(initialValue));
    } else if (!initialValue) {
      setSelectedDrivers([]);
    }
  }, [driversQuery.data, initialValue, isOpen, parseInitialValue]);

  return (
    <>
     <Dialog
        open={isOpen}
        onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: {
              backgroundColor: isDark ? darkColors.surface : secondary[100],
              borderRadius: '15px',
            }
          }}
        >
          <div className="flex flex-col h-full gap-4 p-8">
            <div className="text-center mt-8 text-ink dark:text-ink-dark">
              <h2
                className="text-2xl font-bold  mt-4"
              >
                Borrador de Choferes
              </h2>
              <p className="text-gray-500 text-sm mt-2">Reserva: #{travelId}</p>
            </div>
            <Formik
              innerRef={formRef}
              initialValues={{ preassigned_driver_draft: initialValue }}
              onSubmit={handleSave}
              enableReinitialize
            >
              {({ isSubmitting, touched, errors, dirty }) => (
                <Form className="flex flex-col gap-4">
                  <Field
                    name="driver"
                    component={Autocomplete}
                    className="col-span-2"
                    noOptionsText="No se han encontrado resultados"
                    loadingText="Cargando..."
                    fullWidth
                    options={(driversQuery.data || []).filter(
                      (driver) =>
                        !selectedDrivers.find(
                          (selected) => selected.codigo === driver.codigo
                        )
                    )}
                    getOptionLabel={(option: ChoferPartialModel) =>
                      `${option.codigo} - ${option.nombreCompleto}`
                    }
                    onChange={(
                      event: any,
                      newValue: ChoferPartialModel | null
                    ) => {
                      handleDriverSelect(newValue);
                      // Clear the field after selection
                      if (newValue) {
                        event.target.value = '';
                      }
                    }}
                    renderInput={(params: AutocompleteRenderInputParams) => (
                      <TextField
                        {...params}
                        error={touched['driver'] && !!errors['driver']}
                        helperText={errors['driver']}
                        label="Agregar Chofer"
                        variant="outlined"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <Fragment>
                              {driversQuery.isLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </Fragment>
                          )
                        }}
                      />
                    )}
                  />
                  {selectedDrivers.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <Typography variant="subtitle2" className="mb-2">
                        Choferes para borrardor:
                      </Typography>
                      <div className="flex flex-wrap gap-2">
                        {selectedDrivers.map((driver) => {
                          const driverCode = driver.codigo.toString();
                          return (
                            <Chip
                              key={driver.codigo}
                              label={`${driver.codigo} - ${driver.nombreCompleto}`}
                              onDelete={() => handleRemoveDriver(driverCode)}
                              deleteIcon={
                                <DeleteIcon
                                  style={{
                                    color: '#ef4444',
                                    transition: 'color 0.2s ease-in-out'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.color = '#dc2626';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.color = '#ef4444';
                                  }}
                                />
                              }
                              variant="outlined"
                              style={{
                                backgroundColor: '#f1f1f1',
                                color: '#495057'
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div
                    className="flex justify-between gap-2 pt-4"
                  >
                    <Button
                      type="button"
                      variant="secondary"
                      color="gray"
                      onClick={handleCloseModal}
                      disabled={isSubmitting}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      Guardar
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </Dialog>
    </>
  );
};

export default DriversNotesModal;

const ButtonDriverDraft = ({
  initialValue,
  handleOpenModal
}: {
  initialValue: string | undefined;
  handleOpenModal: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  return (
    <div
      className="flex justify-between  gap-2 items-center rounded-md"
      style={{
        backgroundColor: initialValue ? '#f1f1f1' : 'transparent'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="pl-2 pr-2">
        {initialValue && (
          <Typography
            variant="body2"
            style={{
              whiteSpace: 'pre-wrap',
              lineHeight: '1.5',
              color: '#495057'
            }}
          >
            {initialValue}
          </Typography>
        )}
      </div>
      <IconButton
        size="small"
        onClick={(e) => handleOpenModal(e)}
        className='p-1 rounded-full text-inkMuted hover:text-black dark:text-inkMuted-dark dark:hover:text-white transition-colors flex-shrink-0'
      >
        <EditIcon fontSize="small" />
      </IconButton>
    </div>
  );
};
export { ButtonDriverDraft };
